import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  useCreateOrgAction,
  useUpdateOrgAction,
} from 'components/Actions/hooks/orgActionHooks';
import type { CustomEventAction } from 'hooks/customEvents/useCustomEvents';
import EventActionDialog, { type EventActionInput } from './EventActionDialog';
import EventActionRow from './EventActionRow';
import { removeTaskConfirmation } from './eventConfirmations';

type EventResponsePlanProps = {
  eventId: UUID;
  actions: CustomEventAction[];
  isLoading: boolean;
  isError: boolean;
};

const EventResponsePlan: React.FC<EventResponsePlanProps> = ({
  eventId,
  actions,
  isLoading,
  isError,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<CustomEventAction>();
  const [removingAction, setRemovingAction] = useState<CustomEventAction>();
  const createAction = useCreateOrgAction();
  const updateAction = useUpdateOrgAction();
  const removeText = removeTaskConfirmation(removingAction?.name ?? '');

  const openDialog = (action?: CustomEventAction) => {
    setEditingAction(action);
    setDialogOpen(true);
  };

  const handleSubmit = async (input: EventActionInput) => {
    try {
      if (editingAction) {
        await updateAction.mutateAsync({
          ...input,
          actionId: editingAction.actionId,
        });
      } else {
        await createAction.mutateAsync({ ...input, checklist: [], eventId });
      }
      setDialogOpen(false);
    } catch {
      // Error toast is shown by the org action hooks; keep the dialog open
    }
  };

  const handleRemove = () => {
    if (!removingAction) return;
    const { actionId } = removingAction;
    setRemovingAction(undefined);
    updateAction.mutate({ actionId, archived: true });
  };

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="tw:font-semibold">Response Plan</CardTitle>
        <CardAction>
          <Button variant="outline" size="sm" onClick={() => openDialog()}>
            <Plus data-icon="inline-start" />
            Add
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="tw:text-muted-foreground">
            Loading response plan...
          </div>
        ) : isError ? (
          <div className="tw:text-destructive">
            Could not load the response plan.
          </div>
        ) : actions.length > 0 ? (
          <div className="tw:grid tw:gap-2">
            {actions.map((action) => (
              <EventActionRow
                key={action.actionId}
                action={action}
                onEdit={openDialog}
                onRemove={setRemovingAction}
              />
            ))}
          </div>
        ) : (
          <div className="tw:text-muted-foreground">
            No response plan configured.
          </div>
        )}
      </CardContent>
      <EventActionDialog
        open={dialogOpen}
        action={editingAction}
        isSaving={createAction.isPending || updateAction.isPending}
        onOpenChange={setDialogOpen}
        onSubmit={(input) => void handleSubmit(input)}
      />
      <AlertDialog
        open={!!removingAction}
        onOpenChange={(open) => !open && setRemovingAction(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{removeText.dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {removeText.dialogContent}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{removeText.dialogCloseText}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleRemove}>
              {removeText.dialogButtonText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EventResponsePlan;
